"use client";

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setInView(true);obs.disconnect();}},
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const residentialServices = [
{
  title: '新成屋客變',
  titleEn: 'New Home Customization',
  desc: '在交屋前介入，與建商協調格局調整、材料升級、設備選配，讓你的新家從一開始就是你的樣子。',
  points: ['格局重新規劃', '建材升級選配', '廚衛設備優化', '燈光系統規劃'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_13a9fb157-1773145946892.png",
  imgAlt: 'New home customization — bright modern living room with clean lines'
},
{
  title: '老屋翻新',
  titleEn: 'Renovation',
  desc: '保留空間的歷史溫度，同時注入現代的生活機能。我們擅長在有限預算內，讓老屋煥然一新。',
  points: ['結構評估與改造', '水電管線更新', '隔間重新配置', '整體風格更新'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f0bba493-1773145947880.png",
  imgAlt: 'Renovation project — transformed older apartment with warm tones'
},
{
  title: '預售屋客變',
  titleEn: 'Pre-sale Customization',
  desc: '在預售屋交屋前，根據平面圖提早規劃，避免二次裝修浪費，一步到位打造理想空間。',
  points: ['平面圖提前規劃', '建商溝通協調', '避免二次裝修', '材料預先確認'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_19dd0bf25-1773145950941.png",
  imgAlt: 'Pre-sale customization — Nordic minimal apartment interior'
},
{
  title: '系統家具整合',
  titleEn: 'System Furniture Integration',
  desc: '量身訂製系統櫃、床架、書桌、衣櫃等，與空間完美融合，最大化收納與美觀。',
  points: ['全屋收納規劃', '系統櫃客製設計', '材質色彩搭配', '安裝監工服務'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_11f0a4a60-1773145946366.png",
  imgAlt: 'System furniture — custom built-in storage with warm wood tones'
}];


const commercialServices = [
{
  title: '咖啡廳 / 餐廳',
  titleEn: 'Café & Restaurant',
  desc: '打造有個性的餐飲空間，讓顧客在用餐的同時感受品牌溫度。從吧台設計到燈光氛圍，每個細節都是品牌故事的一部分。',
  points: ['品牌視覺整合', '動線與座位規劃', '廚房功能設計', '燈光氛圍營造'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_138c16ad5-1773145947208.png",
  imgAlt: 'Café interior design — warm branded coffee shop with cozy atmosphere'
},
{
  title: '診所 / 醫療空間',
  titleEn: 'Clinic & Medical',
  desc: '兼顧專業形象與患者舒適感。我們深知醫療空間的特殊需求，設計出讓人放鬆、同時符合規範的診療環境。',
  points: ['候診區舒適設計', '診療室功能規劃', '符合衛生標準', '導視系統設計'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_13aa9211e-1772148515180.png",
  imgAlt: 'Clinic interior — clean professional medical space with calming design'
},
{
  title: '辦公室',
  titleEn: 'Office',
  desc: '設計激發創意與協作的工作環境。從開放式工位到私密會議室，我們幫你打造讓團隊愛上上班的辦公空間。',
  points: ['工作區域規劃', '會議室設計', '品牌形象融入', '人體工學考量'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f675c34d-1767121589139.png",
  imgAlt: 'Office interior design — modern collaborative workspace with natural light'
},
{
  title: '品牌展廳',
  titleEn: 'Brand Showroom',
  desc: '讓你的產品在最好的舞台上被看見。我們設計能說故事的展示空間，讓每一位訪客都成為品牌的擁護者。',
  points: ['展示動線設計', '陳列方式規劃', '品牌故事呈現', '燈光展示優化'],
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_16d250ac4-1773145947587.png",
  imgAlt: 'Brand showroom — elegant retail display space with curated lighting'
}];


const philosophyPillars = [
{
  no: '01',
  title: '溫暖優先',
  titleEn: 'Warmth First',
  desc: '材質、色調、光線、動線，都以「讓人感到被擁抱」為第一考量。好的空間不只是好看，而是讓人想一直待著。',
  icon: '◎'
},
{
  no: '02',
  title: '細節控',
  titleEn: 'Detail Obsessed',
  desc: '從隱藏式收納到插座位置，從燈光層次到把手觸感，我們相信細節決定舒適度。每個你注意不到的細節，都是我們花時間思考過的。',
  icon: '◈'
},
{
  no: '03',
  title: '永續思維',
  titleEn: 'Sustainable',
  desc: '選用環保、耐用、可循環的材料，讓美觀與責任並存。我們認為好的設計不應該以犧牲環境為代價。',
  icon: '◇'
},
{
  no: '04',
  title: '跨界融合',
  titleEn: 'Cross-Style',
  desc: '不侷限單一風格，北歐的簡約、日式的留白、工業的粗獷、現代的俐落……只要適合你，我們都願意嘗試與混搭。',
  icon: '◉'
}];


const processSteps = [
{ no: '01', title: '初步諮詢', desc: '免費了解您的需求、空間條件與預算範圍，確認合作方向。', duration: '1–2小時' },
{ no: '02', title: '現場勘查', desc: '實地測量空間，了解結構限制與光線條件，收集設計素材。', duration: '2–3小時' },
{ no: '03', title: '概念提案', desc: '提交設計概念圖、色彩板、材料樣本，與您共同確認方向。', duration: '1–2週' },
{ no: '04', title: '施工圖繪製', desc: '完整施工圖、水電配置圖、家具配置圖，確保施工精確無誤。', duration: '2–3週' },
{ no: '05', title: '施工監管', desc: '全程監工，確保施工品質符合設計規範，即時處理現場問題。', duration: '依項目規模' },
{ no: '06', title: '完工驗收', desc: '細項驗收、問題修繕、最終軟裝佈置，交付你的理想空間。', duration: '1–3天' }];


export default function BusinessScope() {
  const sec1 = useInView();
  const sec2 = useInView();
  const sec3 = useInView();
  const sec4 = useInView();
  const sec5 = useInView();
  const [activeTab, setActiveTab] = useState<'residential' | 'commercial'>('residential');

  return (
    <>
      <Header />

      {/* ── MINIMAL PAGE HEADER ──────────────────────────── */}
      <section className="pt-32 pb-16 bg-background border-b border-border">
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="tag-pill">Business Scope</span>
          </div>
          <h1 className="font-heading font-700 text-h1 text-primary mb-3">
            業務範圍
          </h1>
          <p className="font-body text-base text-secondary max-w-xl">
            從住宅到商業空間，從新成屋到老屋翻新，我們提供全方位的室內設計服務。
          </p>
        </div>
      </section>

      {/* ── SERVICE TABS ─────────────────────────────────── */}
      <section
        ref={sec1.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          {/* Tab switcher */}
          <div
            className="flex gap-0 mb-16 border-b border-border"
            style={{
              opacity: sec1.inView ? 1 : 0,
              transform: sec1.inView ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease'
            }}>
            
            {[
            { key: 'residential', label: '住宅空間', labelEn: 'Residential' },
            { key: 'commercial', label: '商業空間', labelEn: 'Commercial' }].
            map((tab) =>
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'residential' | 'commercial')}
              className={`relative px-8 py-4 font-heading text-base font-600 transition-all duration-200 ${
              activeTab === tab.key ?
              'text-primary' : 'text-muted hover:text-secondary'}`
              }>
              
                {tab.label}
                <span className="font-mono text-xs ml-2 opacity-50">{tab.labelEn}</span>
                {activeTab === tab.key &&
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" />
              }
              </button>
            )}
          </div>

          {/* Service cards */}
          <div className="space-y-16">
            {(activeTab === 'residential' ? residentialServices : commercialServices).map((service, i) =>
            <div
              key={service.title}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
              i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`
              }
              style={{
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`
              }}>
              
                {/* Image */}
                <div className={`relative overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`} style={{ borderRadius: '4px' }}>
                  <div className="relative aspect-[4/3]">
                    <AppImage
                    src={service.img}
                    alt={service.imgAlt}
                    fill
                    className="object-cover transition-transform duration-700 hover:scale-105" />
                  
                    {/* Number overlay */}
                    <div className="absolute top-4 left-4 glass-dark px-3 py-1.5" style={{ borderRadius: '2px' }}>
                      <span className="font-mono text-xs text-white/60">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className="tag-pill mb-4 inline-block">{service.titleEn}</span>
                  <h3 className="font-heading text-h3 font-700 text-primary mb-4">{service.title}</h3>
                  <p className="font-body text-base text-secondary leading-relaxed mb-6">{service.desc}</p>

                  {/* Points */}
                  <ul className="space-y-3 mb-8">
                    {service.points.map((point) =>
                  <li key={point} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                        <span className="font-body text-sm text-secondary">{point}</span>
                      </li>
                  )}
                  </ul>

                  <Link href="/registration-center" className="btn-outline inline-flex">
                    諮詢此服務
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── DESIGN PHILOSOPHY ────────────────────────────── */}
      <section
        ref={sec2.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-primary relative overflow-hidden">
        
        {/* Background decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-5 pointer-events-none">
          <svg viewBox="0 0 400 600" className="w-full h-full" fill="none">
            <circle cx="300" cy="300" r="250" stroke="white" strokeWidth="1" />
            <circle cx="300" cy="300" r="180" stroke="white" strokeWidth="1" />
            <circle cx="300" cy="300" r="110" stroke="white" strokeWidth="1" />
          </svg>
        </div>

        <div className="relative z-10 max-w-container mx-auto px-6 lg:px-12">
          <div
            className="mb-16"
            style={{
              opacity: sec2.inView ? 1 : 0,
              transform: sec2.inView ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill border-white/20 text-white/40 mb-4 inline-block">Design Philosophy</span>
            <h2 className="font-heading font-700 text-h2 text-white">設計哲學</h2>
            <p className="font-body text-base text-white/50 mt-3 max-w-lg">
              這四個原則，是我們在每一個設計決定背後的思考框架。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {philosophyPillars.map((p, i) =>
            <div
              key={p.no}
              className="p-8 border border-white/10 group hover:border-accent/40 hover:bg-white/5 transition-all duration-300"
              style={{
                borderRadius: '4px',
                opacity: sec2.inView ? 1 : 0,
                transform: sec2.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`
              }}>
              
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="font-mono text-xs text-white/20 block mb-2">{p.no}</span>
                    <h3 className="font-heading text-xl font-700 text-white">{p.title}</h3>
                    <p className="font-mono text-xs text-accent/70 mt-1 tracking-wide">{p.titleEn}</p>
                  </div>
                  <span className="text-3xl text-accent/30 group-hover:text-accent transition-colors duration-300">
                    {p.icon}
                  </span>
                </div>
                <p className="font-body text-sm text-white/50 leading-relaxed group-hover:text-white/70 transition-colors duration-300">
                  {p.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PROCESS ──────────────────────────────────────── */}
      <section
        ref={sec3.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div
            className="mb-16"
            style={{
              opacity: sec3.inView ? 1 : 0,
              transform: sec3.inView ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill mb-4 inline-block">Our Process</span>
            <h2 className="font-heading font-700 text-h2 text-primary">合作流程</h2>
            <p className="font-body text-base text-secondary mt-3 max-w-lg">
              從初次接觸到完工交付，我們的每一步都有清晰的節奏與溝通。
            </p>
          </div>

          {/* Process grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {processSteps.map((step, i) =>
            <div
              key={step.no}
              className="bg-background p-8 group hover:bg-surface transition-colors duration-300 relative overflow-hidden"
              style={{
                opacity: sec3.inView ? 1 : 0,
                transform: sec3.inView ? 'translateY(0)' : 'translateY(30px)',
                transition: `opacity 0.7s ease ${i * 0.08}s, transform 0.7s ease ${i * 0.08}s`
              }}>
              
                <div className="flex items-start justify-between mb-4">
                  <span className="font-mono text-3xl font-300 text-border group-hover:text-accent/40 transition-colors duration-300">
                    {step.no}
                  </span>
                  <span className="tag-pill text-muted">{step.duration}</span>
                </div>
                <h4 className="font-heading text-lg font-700 text-primary mb-3">{step.title}</h4>
                <p className="font-body text-sm text-secondary leading-relaxed">{step.desc}</p>
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── STATS BANNER ─────────────────────────────────── */}
      <section
        ref={sec4.ref as React.RefObject<HTMLElement>}
        className="py-20 bg-surface border-y border-border">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x lg:divide-border">
            {[
            { value: '120+', label: '完成項目', labelEn: 'Projects Completed' },
            { value: '98%', label: '客戶滿意度', labelEn: 'Client Satisfaction' },
            { value: '8年', label: '設計經驗', labelEn: 'Years Experience' },
            { value: '2日', label: '回覆時間', labelEn: 'Response Time' }].
            map((stat, i) =>
            <div
              key={stat.label}
              className="text-center lg:px-8 group cursor-default"
              style={{
                opacity: sec4.inView ? 1 : 0,
                transform: sec4.inView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.7s ease ${i * 0.1}s, transform 0.7s ease ${i * 0.1}s`
              }}>
              
                <div className="font-heading text-5xl lg:text-6xl font-700 text-primary group-hover:text-accent transition-colors duration-300 mb-2">
                  {stat.value}
                </div>
                <p className="font-body text-sm font-500 text-foreground">{stat.label}</p>
                <p className="font-mono text-xs text-muted mt-0.5">{stat.labelEn}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── BRIDGE CTA ───────────────────────────────────── */}
      <section
        ref={sec5.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: image */}
            <div
              className="relative overflow-hidden"
              style={{
                borderRadius: '4px',
                opacity: sec5.inView ? 1 : 0,
                transform: sec5.inView ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'opacity 0.9s ease, transform 0.9s ease'
              }}>
              
              <div className="relative aspect-[16/9]">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_11f0b83c0-1773145948287.png"
                  alt="Interior design consultation — designer reviewing plans with client"
                  fill
                  className="object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-transparent" />
              </div>
            </div>

            {/* Right: CTA content */}
            <div
              style={{
                opacity: sec5.inView ? 1 : 0,
                transform: sec5.inView ? 'translateX(0)' : 'translateX(30px)',
                transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s'
              }}>
              
              <span className="tag-pill mb-6 inline-block">Start Today</span>
              <h2 className="font-heading font-700 text-h2 text-primary mb-5">
                找到你的
                <br />
                <span className="text-gradient-gold">理想空間方案</span>
              </h2>
              <p className="font-body text-base text-secondary leading-relaxed mb-8 max-w-md">
                不確定自己需要哪種服務？沒關係，預約一次免費諮詢，
                我們一起討論最適合你的方向。
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/registration-center" className="btn-accent">
                  預約免費諮詢
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {/* Contact strip */}
              <div className="mt-8 pt-8 border-t border-border flex items-center gap-6">
                <a
                  href="mailto:macklavelle14@gmail.com"
                  className="font-body text-sm text-secondary hover:text-accent transition-colors flex items-center gap-2">
                  
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  macklavelle14@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>);

}